import React, { useMemo, useState } from 'react';
import { useTheme } from '@/theme';
import { Card, Flex, Grid, Spinner, Text, Tag, Button } from '@/components/ui';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import { DataTable } from '@/components/common/DataTable';
import { MenuPath } from '@/components/ui/common/MenuPath';
import {
  useSystemStatus,
  type DeviceRedisInfo,
  type KeyCategory,
} from '@/api/admin/useSystemStatus';
import {
  useAdminFcmTokens,
  useDeleteFcmToken,
  useDeleteUserFcmTokens,
  type AdminWebTokenRow,
} from '@/api/admin/useAdminFcmTokens';
import { useNotificationStore } from '@/store/notificationStore';
import type { ColumnDef } from '@tanstack/react-table';

const formatTtl = (seconds: number): string => {
  if (seconds <= 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}일 ${hours}시간`;
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  return `${minutes}분`;
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}일 ${hours}시간 ${minutes}분`;
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  return `${minutes}분`;
};

const formatLastSeen = (lastSeen: string | null): string => {
  if (!lastSeen) return '—';
  const date = new Date(lastSeen);
  return date.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const SystemStatusPage: React.FC = () => {
  const { theme } = useTheme();
  const { data, isLoading } = useSystemStatus();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const { data: fcmTokens = [], isLoading: isFcmLoading } = useAdminFcmTokens();
  const deleteFcmToken = useDeleteFcmToken();
  const deleteUserFcmTokens = useDeleteUserFcmTokens();
  const { showSuccess, showError } = useNotificationStore();

  const handleDeleteToken = (row: AdminWebTokenRow) => {
    const label = `${row.user_name || row.email || `user ${row.user_id}`} · ${row.browser || 'Unknown'}`;
    if (!window.confirm(`이 토큰을 삭제할까요?\n${label}`)) return;
    deleteFcmToken.mutate(
      { user_id: row.user_id, token: row.token },
      {
        onSuccess: () => showSuccess('토큰을 삭제했습니다.'),
        onError: (e) => showError('토큰 삭제 실패', (e as Error).message),
      }
    );
  };

  const handleDeleteUserTokens = (row: AdminWebTokenRow) => {
    const label = row.user_name || row.email || `user ${row.user_id}`;
    if (!window.confirm(`${label}의 모든 웹 토큰을 삭제할까요?`)) return;
    deleteUserFcmTokens.mutate(
      { user_id: row.user_id },
      {
        onSuccess: () => showSuccess('해당 사용자의 모든 토큰을 삭제했습니다.'),
        onError: (e) => showError('토큰 삭제 실패', (e as Error).message),
      }
    );
  };

  const fcmColumns: ColumnDef<AdminWebTokenRow, any>[] = useMemo(
    () => [
      {
        id: 'user',
        header: '사용자',
        size: 180,
        cell: ({ row }) => {
          const d = row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13 }}>{d.user_name || '—'}</span>
              <span style={{ fontSize: 11, color: '#888' }}>{d.email || `user ${d.user_id}`}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'role_name',
        header: '역할',
        size: 120,
        cell: ({ getValue }) => {
          const role = getValue<string | null>();
          if (!role) return '—';
          const variant =
            role === 'SuperAdmin' || role === 'Admin'
              ? 'primary'
              : role === 'CompanyManager'
                ? 'success'
                : 'info';
          return (
            <Tag variant={variant} size="sm">
              {role}
            </Tag>
          );
        },
      },
      {
        accessorKey: 'company_name',
        header: '회사',
        size: 120,
        cell: ({ getValue }) => getValue<string | null>() ?? '—',
      },
      {
        id: 'sites',
        header: '사이트',
        size: 140,
        cell: ({ row }) => {
          const sites = row.original.site_names;
          if (!sites || sites.length === 0) return '—';
          return <span style={{ fontSize: 12 }}>{sites.join(', ')}</span>;
        },
      },
      {
        id: 'device',
        header: '브라우저 / OS',
        size: 150,
        cell: ({ row }) => {
          const d = row.original;
          const browser = d.browser || 'Unknown';
          const os = d.os ? ` / ${d.os}` : '';
          return (
            <span title={d.user_agent} style={{ fontSize: 12 }}>
              {browser}
              {os}
            </span>
          );
        },
      },
      {
        accessorKey: 'origin',
        header: 'Origin',
        size: 160,
        cell: ({ getValue }) => (
          <span style={{ fontSize: 11, color: '#888' }}>{getValue<string>()}</span>
        ),
      },
      {
        accessorKey: 'language',
        header: '언어',
        size: 60,
        cell: ({ getValue }) => getValue<string>() ?? '—',
      },
      {
        id: 'is_latest',
        header: '수신',
        size: 90,
        cell: ({ row }) =>
          row.original.is_latest ? (
            <Tag variant="success" size="sm">
              발송 대상
            </Tag>
          ) : (
            <Tag variant="info" size="sm">
              대기
            </Tag>
          ),
      },
      {
        accessorKey: 'updated_at',
        header: '등록/갱신',
        size: 140,
        cell: ({ getValue }) => (
          <span style={{ fontSize: 12 }}>{formatLastSeen(getValue<string | null>())}</span>
        ),
      },
      {
        accessorKey: 'ttl_seconds',
        header: '만료까지',
        size: 100,
        cell: ({ getValue }) => (
          <span style={{ fontSize: 12 }}>{formatTtl(getValue<number>())}</span>
        ),
      },
      {
        id: 'actions',
        header: '삭제',
        size: 130,
        cell: ({ row }) => (
          <Flex gap={1}>
            <Button
              variant="tertiary"
              width="auto"
              style={{ padding: '2px 8px', fontSize: 12 }}
              onClick={() => handleDeleteToken(row.original)}
            >
              토큰
            </Button>
            <Button
              variant="tertiaryNeutral"
              width="auto"
              style={{ padding: '2px 8px', fontSize: 12 }}
              onClick={() => handleDeleteUserTokens(row.original)}
            >
              전체
            </Button>
          </Flex>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const deviceColumns: ColumnDef<DeviceRedisInfo, any>[] = useMemo(
    () => [
      {
        accessorKey: 'serial_number',
        header: 'Serial Number',
        size: 160,
        cell: ({ getValue }) => (
          <span
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12,
            }}
          >
            {getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'device_type',
        header: '기기 타입',
        size: 100,
        cell: ({ getValue }) => getValue<string>() ?? '—',
      },
      {
        accessorKey: 'site_name',
        header: '사이트',
        size: 120,
        cell: ({ getValue }) => getValue<string>() ?? '미배정',
      },
      {
        accessorKey: 'status',
        header: '등록 상태',
        size: 90,
        cell: ({ getValue }) => {
          const status = getValue<string>();
          const variant =
            status === 'active' ? 'success' : status === 'unregistered' ? 'warning' : 'info';
          return (
            <Tag variant={variant} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'connection_state',
        header: 'Redis 상태',
        size: 110,
        cell: ({ row }) => {
          const d = row.original;
          if (d.explicit_disconnect) {
            return (
              <Tag variant="warning" size="sm">
                명시적 해제
              </Tag>
            );
          }
          if (d.is_online) {
            return (
              <Tag variant="success" size="sm">
                ON
              </Tag>
            );
          }
          return (
            <Tag variant="info" size="sm">
              OFF
            </Tag>
          );
        },
      },
      {
        accessorKey: 'db_is_connected',
        header: 'DB 값',
        size: 80,
        cell: ({ getValue }) => {
          const val = getValue<boolean>();
          if (val) {
            return (
              <Tag variant="success" size="sm">
                ON
              </Tag>
            );
          }
          return (
            <Tag variant="info" size="sm">
              OFF
            </Tag>
          );
        },
      },
      {
        id: 'db_sync',
        header: 'DB 동기화',
        size: 90,
        cell: ({ row }) => {
          const d = row.original;
          if (d.synced) {
            return (
              <Tag variant="success" size="sm">
                OK
              </Tag>
            );
          }
          return (
            <Tag variant="error" size="sm">
              불일치
            </Tag>
          );
        },
      },
      {
        accessorKey: 'last_seen',
        header: 'Last Seen',
        size: 140,
        cell: ({ getValue }) => (
          <span style={{ fontSize: 12 }}>{formatLastSeen(getValue<string | null>())}</span>
        ),
      },
      {
        accessorKey: 'ttl_seconds',
        header: 'TTL',
        size: 70,
        cell: ({ getValue }) => {
          const ttl = getValue<number>();
          if (ttl <= 0) return '—';
          return `${ttl}s`;
        },
      },
    ],
    []
  );

  if (isLoading || !data) {
    return (
      <>
        <MenuPath />
        <DefaultContents>
          <Flex align="center" justify="center" style={{ padding: theme.spacing[8] }}>
            <Spinner size="md" />
            <Text color="textSecondary" size="sm">
              로딩 중...
            </Text>
          </Flex>
        </DefaultContents>
      </>
    );
  }

  const { redis, devices } = data;

  return (
    <>
      <DefaultContents>
        {/* Redis 상태 요약 */}
        <Grid columns={4} gap={4}>
          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              Redis 상태
            </Text>
            <Flex align="center" gap={2}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: redis.connected ? '#22c55e' : '#ef4444',
                }}
              />
              <Text size="xl">{redis.connected ? '연결됨' : '끊김'}</Text>
            </Flex>
            <Text size="xs" color="textSecondary">
              v{redis.version}
            </Text>
          </Card>

          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              Uptime
            </Text>
            <Text size="xl">{formatUptime(redis.uptime_seconds)}</Text>
          </Card>

          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              메모리
            </Text>
            <Text size="xl">{redis.used_memory_human}</Text>
            <Text size="xs" color="textSecondary">
              peak: {redis.used_memory_peak_human}
            </Text>
          </Card>

          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              Total Keys
            </Text>
            <Text size="xl">{redis.total_keys}</Text>
          </Card>
        </Grid>

        {/* Redis 키 분류 */}
        <Card padding={6} title="Redis 키 상세">
          <Flex
            direction="column"
            gap={3}
            align="stretch"
            style={{ textAlign: 'left', width: '100%' }}
          >
            {redis.keys_by_category.map((cat: KeyCategory) => (
              <div key={cat.prefix}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                    background: theme.colors.surface.layout.overlay,
                    borderRadius: theme.borderRadius.sm,
                    cursor: cat.count > 0 ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (cat.count > 0) {
                      setExpandedCategory(expandedCategory === cat.prefix ? null : cat.prefix);
                    }
                  }}
                >
                  <Flex align="center" gap={3}>
                    <Text size="sm" style={{ fontWeight: theme.fontWeight.medium }}>
                      {cat.label}
                    </Text>
                    <Text
                      size="xs"
                      color="textSecondary"
                      style={{
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      {cat.prefix}
                    </Text>
                  </Flex>
                  <Tag variant={cat.count > 0 ? 'primary' : 'info'} size="sm">
                    {cat.count}개
                  </Tag>
                </div>

                {expandedCategory === cat.prefix && cat.keys.length > 0 && (
                  <div
                    style={{
                      margin: `${theme.spacing[2]} 0 0 ${theme.spacing[4]}`,
                      padding: theme.spacing[3],
                      background: theme.colors.surface.layout.background,
                      border: `1px solid ${theme.colors.border.container}`,
                      borderRadius: theme.borderRadius.sm,
                      maxHeight: 200,
                      overflowY: 'auto',
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 11,
                      lineHeight: 1.6,
                    }}
                  >
                    {cat.keys.map(key => (
                      <div key={key} style={{ color: theme.colors.text.secondary }}>
                        {key}
                      </div>
                    ))}
                    {cat.count > 50 && (
                      <Text size="xs" color="textSecondary" style={{ marginTop: theme.spacing[2] }}>
                        ... 외 {cat.count - 50}개
                      </Text>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Flex>
        </Card>

        {/* 디바이스 연결 현황 요약 */}
        <Grid columns={4} gap={4}>
          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              등록 기기
            </Text>
            <Text size="xl">{devices.total_registered}</Text>
          </Card>
          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              온라인
            </Text>
            <Text size="xl" style={{ color: '#22c55e' }}>
              {devices.online_count}
            </Text>
          </Card>

          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              명시적 해제
            </Text>
            <Text size="xl" style={{ color: '#f59e0b' }}>
              {devices.explicit_disconnected_count}
            </Text>
          </Card>
          <Card padding={6}>
            <Text size="sm" color="textSecondary" style={{ marginBottom: theme.spacing[2] }}>
              DB 불일치
            </Text>
            <Text size="xl" style={{ color: devices.mismatch_count > 0 ? '#ef4444' : undefined }}>
              {devices.mismatch_count}
            </Text>
          </Card>
        </Grid>

        {/* 디바이스 목록 테이블 */}
        <Card padding={6} title="디바이스 상세 목록">
          <DataTable
            data={devices.list}
            columns={deviceColumns}
            enablePagination
            pageSize={20}
            enableSorting
            emptyMessage="등록된 디바이스가 없습니다."
          />
        </Card>

        {/* 웹 푸시(FCM) 토큰 목록 — 사용자/브라우저/기기별 */}
        <Card padding={6} title="웹 푸시(FCM) 토큰">
          <Text size="xs" color="textSecondary" style={{ marginBottom: theme.spacing[3] }}>
            사용자별로 등록된 브라우저/기기 토큰입니다. "발송 대상"은 실제 푸시가 가는
            기기(데스크탑·모바일 각각 최신 1개)이며, 같은 플랫폼의 나머지 브라우저는 대기
            상태입니다. 토큰은 미접속 시 만료(TTL)로 자동 정리됩니다.
          </Text>
          {isFcmLoading ? (
            <Flex align="center" justify="center" gap={2} style={{ padding: theme.spacing[6] }}>
              <Spinner size="sm" />
              <Text color="textSecondary" size="sm">
                로딩 중...
              </Text>
            </Flex>
          ) : (
            <DataTable
              data={fcmTokens}
              columns={fcmColumns}
              enablePagination
              pageSize={20}
              enableSorting
              emptyMessage="등록된 웹 푸시 토큰이 없습니다."
            />
          )}
        </Card>
      </DefaultContents>
    </>
  );
};

export default SystemStatusPage;
